import {
  subMonths,
  startOfQuarter,
  lastDayOfQuarter,
  getDate,
  startOfMonth,
  addHours,
} from 'date-fns';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import Loader from './components/loader/loader';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { useAppDispatch, useAppSelector } from './store';
import { attendanceActions, attendanceThunkActions } from './store/attendance';
import { authActions } from './store/auth';
import { caregiverActions, caregiverThunkActions } from './store/caregiver';
import { childrenActions, childrenThunkActions } from './store/children';
import { classroomsActions, classroomsThunkActions } from './store/classroom';
import { activityActions } from './store/content/activity';
import {
  contentConsentActions,
  contentConsentThunkActions,
} from './store/content/consent';
import {
  programmeRoutineActions,
  programmeRoutineThunkActions,
} from './store/content/programme-routine';
import { programmeThemeActions } from './store/content/programme-theme';
import {
  contentReportActions,
  contentReportThunkActions,
} from './store/content/report';
import { storyBookActions } from './store/content/story-book';
import { documentActions, documentThunkActions } from './store/document';
import { notesActions, notesThunkActions } from './store/notes';
import { notificationActions } from './store/notifications';
import {
  progressTrackingActions,
  progressTrackingThunkActions,
} from './store/progress-tracking';
import { settingActions } from './store/settings';
import { staticDataActions } from './store/static-data';
import { userActions, userThunkActions } from './store/user';
import { coachActions, coachThunkActions } from './store/coach';
import {
  practitionerActions,
  practitionerSelectors,
  practitionerThunkActions,
} from './store/practitioner';
import { practitionerForCoachActions } from './store/practitionerForCoach';
import { analyticsActions } from './store/analytics';
import { userSelectors } from '@store/user';
import { useSelector } from 'react-redux';
import {
  childrenForPractitionerActions,
  childrenForPractitionerThunkActions,
} from './store/childrenForPractitioner';
import { classroomsForCoachThunkActions } from './store/classroomForCoach';
import { programmeActions, programmeThunkActions } from './store/programme';
import { traineeSelectors, traineeThunkActions } from './store/trainee';
import { calendarThunkActions } from './store/calendar';
import { getClubForUser } from './store/club/club.actions';
import { clubActions } from './store/club';
import { authSelectors } from '@store/auth';
import { statementsThunkActions } from '@store/statements';
import { queryErrorsActions } from './store/queryErrors';

type IntialStoreSetupContextValues = {
  initloading: boolean;
  initStoreSetup: () => Promise<void>;
  resetAppStore: (showLoading?: boolean, isSync?: boolean) => Promise<void>;
  resetAuth: () => Promise<void>;
  getLoadingMessage: () => string;
  syncClassroom: () => Promise<void>;
  refreshClassroom: () => Promise<void>;
};

export const IntialStoreSetupContext =
  React.createContext<IntialStoreSetupContextValues>(
    {} as IntialStoreSetupContextValues
  );

const InitialStoreSetup: React.FC = ({ children }) => {
  const appDispatch = useAppDispatch();
  const { isOnline } = useOnlineStatus();
  const [initloading, setInitLoading] = useState(false);
  const [staticDataLoading, setStaticDataLoading] = useState(false);
  const userData = useSelector(userSelectors.getUser);
  const userAuth = useSelector(authSelectors.getAuthUser);
  const isCoach = userData?.roles?.some((role) => role.name === 'Coach');
  const isPrincipalFromRoles = userData?.roles?.some(
    (role) => role.name === 'Principal'
  );
  const practitioner = useSelector(practitionerSelectors?.getPractitioner);
  const isPrincipal = practitioner?.isPrincipal;

  const traineeTimeline = useSelector(
    traineeSelectors.getTraineeOnboardTimeline(practitioner?.userId || '')
  );
  const traineeVisits = traineeTimeline?.traineeVisits;
  const traineeCurrentVisit = traineeVisits?.[0];
  const [otherLoading, setOtherLoading] = useState(false);
  const [shouldSaveStateHash, setShouldSaveStateHash] = useState(false);
  const quarterStartDate = startOfQuarter(new Date());
  const quarterLastDay = lastDayOfQuarter(new Date());

  const { sync, analytics, settings, notifications, ...state } = useAppSelector(
    (state) => state
  );

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const resetAuth = async () => {
    appDispatch(authActions.resetAuthState());
  };

  const resetAppStore = async (showLoading = true, isSync = false) => {
    if (showLoading && isMounted.current) {
      setInitLoading(true);
    }
    await resetStaticStoreSetup();
    await resetAdditionalStoreSetup(isSync);
    if (showLoading && isMounted.current) {
      setInitLoading(false);
    }
  };

  const resetStaticStoreSetup = async () => {
    appDispatch(staticDataActions.resetStaticDataState());
    appDispatch(progressTrackingActions.resetProgressTrackingState());
    appDispatch(programmeRoutineActions.resetProgrammeRoutineState());
    appDispatch(activityActions.resetActivityState());
    appDispatch(storyBookActions.resetStoryBookState());
    appDispatch(programmeThemeActions.resetProgrammeThemeState());
    appDispatch(contentConsentActions.resetContentConsentState());
    appDispatch(notificationActions.resetNotificationState());
    appDispatch(settingActions.resetSettingsState());
    appDispatch(analyticsActions.resetAnalyticsState());
    appDispatch(programmeActions.resetProgrammeState());
  };

  const resetAdditionalStoreSetup = async (isSync?: boolean) => {
    if (!isSync) {
      appDispatch(userActions.resetUserState());
    }
    appDispatch(notesActions.resetNotesState());
    appDispatch(classroomsActions.resetClassroomState());
    appDispatch(coachActions.resetCoachState());
    appDispatch(practitionerActions.resetPractitionerState());
    appDispatch(practitionerForCoachActions.resetPractitionerState());
    appDispatch(childrenActions.resetChildrenState());
    appDispatch(
      childrenForPractitionerActions.resetChildrenForPractitionerState()
    );
    appDispatch(caregiverActions.resetCaregiverState());
    appDispatch(documentActions.resetDocumentsState());
    appDispatch(attendanceActions.resetAttendanceState());
    appDispatch(contentReportActions.resetContentReportState());
    appDispatch(clubActions.resetClubState());
    appDispatch(queryErrorsActions.clearErrors());
  };

  const initStoreSetup = useCallback(async () => {
    if (isOnline) {
      if (isMounted.current) setInitLoading(true);
      await initStaticStoreSetup();
      if (isCoach) {
        // await initCoachAdditionalStoreSetup();
      } else {
        await initAdditionalStoreSetup();
      }
      appDispatch(settingActions.setLastDataSync());
      if (isMounted.current) setInitLoading(false);
      if (isMounted.current) setShouldSaveStateHash(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  const initAdditionalStoreSetup = async () => {
    if (isMounted.current) setOtherLoading(true);
    await appDispatch(classroomsThunkActions.getClassroom({})).unwrap();
    await appDispatch(classroomsThunkActions.getClassroomGroups({})).unwrap();
    await appDispatch(
      classroomsThunkActions.getClassroomProgrammes({})
    ).unwrap();
    await appDispatch(
      classroomsThunkActions.getClassroomGroupLearners({})
    ).unwrap();

    await appDispatch(caregiverThunkActions.getCaregivers({})).unwrap();
    await appDispatch(documentThunkActions.getDocuments({})).unwrap();
    await appDispatch(contentReportThunkActions.getDetailedProgressReports(50));
    await appDispatch(
      contentReportThunkActions.getChildProgressReportSummary(50)
    ).unwrap();
    await appDispatch(notesThunkActions.getNotes({})).unwrap();
    await appDispatch(programmeThunkActions.getUserProgrammes({})).unwrap();
    await appDispatch(userThunkActions.getUserConsents({})).unwrap();
    //REMOVED FOR SECURITY
    //await appDispatch(
    // calendarThunkActions.getCalendarEvents({
    //   start: subMonths(
    //     new Date(new Date().getFullYear(), new Date().getMonth(), 0),
    //    1
    //  ),
    //})
    //);
    if (isMounted.current) setOtherLoading(false);
  };

  const initStaticStoreSetup = async () => {
    if (isMounted.current) setStaticDataLoading(true);
    const promises = [
      appDispatch(
        contentConsentThunkActions.getConsent({ locale: 'en-za' })
      ).unwrap(),
      appDispatch(
        progressTrackingThunkActions.getProgressTrackingCategories({
          locale: 'en-za',
        })
      ).unwrap(),
      appDispatch(
        progressTrackingThunkActions.getProgressTrackingSubCategories({
          locale: 'en-za',
        })
      ).unwrap(),
      appDispatch(
        progressTrackingThunkActions.getProgressTrackingSkills({
          locale: 'en-za',
        })
      ).unwrap(),
      appDispatch(
        progressTrackingThunkActions.getProgressTrackingLevels({
          locale: 'en-za',
        })
      ).unwrap(),
      appDispatch(
        programmeRoutineThunkActions.getProgrammeRoutines({ locale: 'en-za' })
      ).unwrap(),
      appDispatch(
        calendarThunkActions.getCalendarEventTypes({ locale: 'en-za' })
      ).unwrap(),
    ];
    Promise.allSettled(promises);

    if (isMounted.current) setStaticDataLoading(false);
  };

  const syncClassroom = async () => {
    await appDispatch(classroomsThunkActions.upsertClassroom({})).unwrap();
    await appDispatch(
      classroomsThunkActions.upsertClassroomGroups({})
    ).unwrap();
    await appDispatch(
      classroomsThunkActions.updateClassroomGroupProgrammes({})
    ).unwrap();
    await appDispatch(
      classroomsThunkActions.updateClassroomGroupLearners({})
    ).unwrap();
  };

  const refreshClassroom = async () => {
    appDispatch(classroomsActions.resetClassroomState());
    await appDispatch(classroomsThunkActions.getClassroom({})).unwrap();
    await appDispatch(classroomsThunkActions.getClassroomGroups({})).unwrap();
    await appDispatch(
      classroomsThunkActions.getClassroomProgrammes({})
    ).unwrap();
    await appDispatch(
      classroomsThunkActions.getClassroomGroupLearners({})
    ).unwrap();
  };

  const getLoadingMessage = () => {
    let message = 'Loading . . .';

    if (staticDataLoading) {
      message = 'Loading static data . . .';
    }

    if (otherLoading) {
      message = 'Loading other data . . .';
    }

    return message;
  };

  const values = {
    initloading,
    initStoreSetup,
    resetAppStore,
    resetAuth,
    getLoadingMessage,
    syncClassroom,
    refreshClassroom,
  };

  useEffect(() => {
    async function init() {
      if (userAuth && !!userAuth.resetData) {
        await resetAppStore();
      }
      await initStoreSetup();
      if (isMounted.current) {
        appDispatch(userActions.updateUserResetData(false));
      }
    }
    init().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userData && !!userData?.id && !isCoach) {
      (async () =>
        await appDispatch(
          practitionerThunkActions.getAllPractitioners({})
        ).unwrap())();
      (async () =>
        await appDispatch(
          practitionerThunkActions.getPractitionerByUserId({
            userId: userData?.id || '',
          })
        ).unwrap())();
      // (async () =>
      //   await appDispatch(
      //     practitionerThunkActions.getPractitionerDisplayMetrics({
      //       userType: 'practitioner',
      //     })
      //   ).unwrap())();
      (async () =>
        await appDispatch(childrenThunkActions.getChildren({})).unwrap())();
      const today = new Date();
      const todaysDateNumber = getDate(today);
      const firstDayPrevMonth = startOfMonth(subMonths(today, 1));
      const firstDayCurrentMonth = startOfMonth(today);
      const firstDayPrevtMonthUpdated = addHours(
        new Date(firstDayPrevMonth),
        8
      );
      const firstDayCurrentMonthUpdated = addHours(
        new Date(firstDayCurrentMonth),
        8
      );

      const attendanceStartDate =
        todaysDateNumber > 0 && todaysDateNumber <= 7
          ? firstDayPrevtMonthUpdated
          : firstDayCurrentMonthUpdated;

      (async () =>
        await appDispatch(
          attendanceThunkActions.getAttendance({
            startDate: attendanceStartDate,
            endDate: new Date(),
          })
        ).unwrap())();
    }
    if (userData) {
      if (isPrincipal) {
        (async () =>
          await appDispatch(
            childrenForPractitionerThunkActions?.getChildrenForPractitioner({
              id: userData?.id!,
            })
          ).unwrap())();
        const startDate = new Date();
        startDate.setFullYear(startDate.getFullYear() - 1);
        (async () =>
          await appDispatch(
            statementsThunkActions.getIncomeStatements({
              startDate: startDate,
              endDate: undefined,
            })
          ).unwrap())();
        (async () =>
          await appDispatch(
            statementsThunkActions.getUnsubmittedIncomeItems({})
          ).unwrap())();
        (async () =>
          await appDispatch(
            statementsThunkActions.getUnsubmittedExpenseItems({})
          ).unwrap())();
      }
    }
  }, [appDispatch, userData, isCoach, isPrincipal]);

  useEffect(() => {
    if (userData) {
      if (practitioner?.coachHierarchy) {
        if (!isCoach) {
          //  (async () =>
          //   await appDispatch(
          //    coachThunkActions.getCoachByCoachId({
          //     coachId: practitioner?.coachHierarchy!,
          //  })
          //).unwrap())();
        }
      }
      if (practitioner?.isTrainee) {
        (async () =>
          await appDispatch(
            traineeThunkActions.getTraineeById({ userId: userData?.id! })
          ).unwrap())();

        (async () =>
          await appDispatch(
            traineeThunkActions.getTraineeTimeline({
              userId: practitioner?.userId ? practitioner?.userId : '',
            })
          ).unwrap())();

        (async () =>
          await appDispatch(
            traineeThunkActions.getTraineeVisitData({
              visitId: traineeCurrentVisit?.id,
            })
          ).unwrap())();
      }
    }
  }, [appDispatch, userData, practitioner, isCoach, traineeCurrentVisit?.id]);

  useEffect(() => {
    if (userData) {
      if (isCoach) {
        //SECURITY_UPGRADE
        /*
         (async () =>
         await appDispatch(coachThunkActions.getCoachByUserId({})).unwrap())();
        (async () =>
          await appDispatch(
            practitionerThunkActions.getAllPractitionersSimple({})
          ).unwrap())();
        (async () =>
          await appDispatch(
            practitionerForCoachThunkActions.getPractitionersForCoach({})
          ).unwrap())();
        (async () =>
          await appDispatch(
            coachThunkActions.getAllCoachingCircleClubsForCoach({
              coachId: userData?.id!,
              startDate: quarterStartDate,
              endDate: quarterLastDay,
            })
          ).unwrap())();
        (async () =>
          await appDispatch(
            coachThunkActions.getAllClubsForCoach({
              userId: userData?.id!,
            })
          ).unwrap())();
        (async (id) =>
          await appDispatch(
            classroomsForCoachThunkActions.getClassroomForCoach({
              id: userData?.id!,
            })
          ).unwrap())();
        (async () =>
          await appDispatch(
            childrenThunkActions.getChildrenForCoach({})
          ).unwrap())();
        (async () =>
          await appDispatch(
            practitionerThunkActions.getPractitionerDisplayMetrics({
              userType: 'coach',
            })
          ).unwrap())();
          */
      }
      /*   if (!isCoach) {
        const currentDate = new Date();
        const oneYearAgo = new Date();
        oneYearAgo.setMonth(currentDate.getMonth() - 12);

        (async () =>
          await appDispatch(
            getClubForUser({ userId: userData?.id! })
          ).unwrap())();
      }*/
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDispatch, userData, isCoach]);

  return (
    <IntialStoreSetupContext.Provider value={values}>
      {!initloading && children}
      {initloading && <Loader loadingMessage={getLoadingMessage()} />}
    </IntialStoreSetupContext.Provider>
  );
};

export default InitialStoreSetup;
